import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "@components/Button";
import { Card } from "@components/Card";
import { CloudinaryImg } from "@components/CloudinaryImg";
import { Icon, IconDisplay, IconSize } from "@components/Icon";
import { ResizingSpan } from "@components/ResizingSpan";
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import {
  formatEinWithDash,
  isValidEin,
} from "@every.org/common/src/entity/types/ein";
import {
  getRoutePath,
  ClientRouteName,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { ImageFormat } from "@every.org/common/src/helpers/cloudinary";
import { displayNonprofitLocation } from "@every.org/common/src/helpers/nonprofitLocation";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { textSizeCss, TextSize, FontWeight } from "src/theme/text";

const AVATAR_PLACEHOLDER_ID = "assets/claim_your_nonprofit_tpydcd";
const AVATAR_PLACEHOLDER_DIMENSIONS = { width: 180, height: 145 };

const StyledCard = styled(Card)`
  ${verticalStackCss.l};
  padding: ${spacing.xl};
  max-width: 688px;
`;

const nonprofitHeaderCss = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.l};

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      align-items: center;
      flex-direction: row;
    `,
  })}
`;

const avatarContainerCss = css`
  flex-shrink: 0;
  width: ${AVATAR_PLACEHOLDER_DIMENSIONS.width}px;
  height: ${AVATAR_PLACEHOLDER_DIMENSIONS.height}px;

  align-self: center;

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      align-self: unset;
    `,
  })}
`;

const nonprofitNameCss = css`
  ${textSizeCss.m};
  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: textSizeCss.xl,
  })}
`;

const secondaryTextCss = css`
  color: var(${colorCssVars.text.secondary});
`;

const benefitsContainerCss = css`
  ${verticalStackCss.l};
  padding: 0;

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      padding: 0 ${spacing.m};
    `,
  })}
`;

const benefitListCss = css`
  ${verticalStackCss.m};

  padding: 0;

  li {
    ${horizontalStackCss.m};
    align-items: center;
  }

  svg {
    flex-shrink: 0;
  }

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      padding: 0 ${spacing.m};
    `,
  })}
`;

const buttonRowCss = [
  horizontalStackCss.m,
  css`
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
  `,
];

interface GetStartedCTACardProps {
  nonprofit: ContextNonprofit;
  className?: string;
}

export function GetStartedCTACard({
  nonprofit,
  className,
}: GetStartedCTACardProps) {
  const locationToDisplay = nonprofit.locationAddress
    ? displayNonprofitLocation({ locationAddress: nonprofit.locationAddress })
    : undefined;

  const nonprofitInfoLine =
    nonprofit.ein && isValidEin(nonprofit.ein)
      ? `A 501(c)(3) nonprofit, EIN ${formatEinWithDash(nonprofit.ein)}`
      : undefined;

  return (
    <StyledCard className={className}>
      <div css={nonprofitHeaderCss}>
        <div css={avatarContainerCss}>
          <CloudinaryImg
            cloudinaryId={AVATAR_PLACEHOLDER_ID}
            alt="Nonprofit profile Avatar Placeholder"
            imageFormat={ImageFormat.svg}
            dimensions={AVATAR_PLACEHOLDER_DIMENSIONS}
          />
        </div>
        <div css={verticalStackCss.xxs}>
          <h1 css={nonprofitNameCss}>
            <ResizingSpan
              css={{ width: "100%" }}
              maxTextSize={TextSize.l}
              minTextSize={TextSize.s}
            >
              {nonprofit.name}
            </ResizingSpan>
          </h1>
          {locationToDisplay && (
            <p css={[secondaryTextCss, { fontWeight: FontWeight.MEDIUM }]}>
              {locationToDisplay}
            </p>
          )}
          {nonprofitInfoLine && (
            <p css={secondaryTextCss}>{nonprofitInfoLine}</p>
          )}
        </div>
      </div>
      <div css={benefitsContainerCss}>
        <p css={textSizeCss.m}>
          Do you work for {nonprofit.name}? Create your profile to:
        </p>
        <ul css={benefitListCss}>
          <li>
            <Icon
              iconImport={() => import("@components/Icon/icons/ButtonIcon")}
              display={IconDisplay.ACCENT}
              size={IconSize.MEDIUM}
            />
            <span>Add donate buttons to your website</span>
          </li>
          <li>
            <Icon
              iconImport={() => import("@components/Icon/icons/DollarIcon")}
              display={IconDisplay.ACCENT}
              size={IconSize.MEDIUM}
            />
            <span>
              Accept donations via card, bank, PayPal, Venmo, Apple or Google
              Pay, crypto, stock, DAF, and IRA
            </span>
          </li>
          <li>
            <Icon
              iconImport={() =>
                import("@components/Icon/icons/ThermometerIcon")
              }
              display={IconDisplay.ACCENT}
              size={IconSize.MEDIUM}
            />
            <span>
              Launch fundraisers or have your supporters fundraise for you
            </span>
          </li>
          <li>
            <Icon
              iconImport={() => import("@components/Icon/icons/ChequeIcon")}
              display={IconDisplay.ACCENT}
              size={IconSize.MEDIUM}
            />
            <span>Set up fast, free disbursements</span>
          </li>
        </ul>
      </div>

      <div css={buttonRowCss}>
        <Button
          data-tname="nonprofitUnclaimedLearnMore"
          role={ButtonRole.TEXT_ONLY}
          size={ButtonSize.MEDIUM}
          onClick={{
            kind: ButtonTargetKind.LINK,
            to: "/nonprofits",
          }}
        >
          Learn more
        </Button>
        <Button
          data-tname="nonprofitUnclaimedGetStarted"
          role={ButtonRole.PRIMARY}
          size={ButtonSize.MEDIUM}
          onClick={{
            kind: ButtonTargetKind.LINK,
            to: getRoutePath({
              name: ClientRouteName.NONPROFITS_ONBOARDING_INTRO,
              format: URLFormat.RELATIVE,
              tokens: {
                nonprofitSlug: nonprofit.primarySlug,
              },
            }),
          }}
        >
          Get started
        </Button>
      </div>
    </StyledCard>
  );
}
